import { Component, OnDestroy, OnInit } from "@angular/core";
import { Subscription } from "rxjs";
import { NotifierService } from "src/app/_services/notifier.service";
import { APIService } from "src/app/api/api.service";
import { environment } from "src/environments/environment";

interface AzureTellMeRule {
  rule_id: string;
  rule_name: string;
  s_no: number;
  enabled: boolean;
  initialEnabled: boolean;
  schedule_key: string;
  group_key: string;
  rule_group_path: string;
  saving?: boolean;
}

interface AzureTellMeGroup {
  key: string;
  name: string;
  rules: AzureTellMeRule[];
}

interface AzureTellMeSchedule {
  key: string;
  name: string;
  groups: AzureTellMeGroup[];
}

@Component({
  selector: "app-azure-tell-me",
  templateUrl: "./azure-tell-me.component.html",
  styleUrls: [
    "./azure-tell-me.component.css",
    "./../../tell-me/tell-me.component.css",
    "./../../ticket/ticket.component.css",
  ],
})
export class AzureTellMeComponent implements OnInit, OnDestroy {
  userId: string = localStorage.getItem("selectedClientList");
  subscriptionId: string = localStorage.getItem("accountId");
  urlPrefix: string = localStorage.getItem("role") == "Admin" ? "admin" : "client";
  writeAccess: boolean =
    localStorage.getItem("acT") == "readandwrite" ||
    this.urlPrefix == "admin" ||
    localStorage.getItem("ut") == "admin";

  loading: boolean = true;
  errorMessage: string = null;
  scheduleData: AzureTellMeSchedule[] = [];
  savingTellMe: boolean = false;
  savingEmails: boolean = false;
  additionalContacts: string[] = [""];
  existingEmailIds: string[] = [];

  selectedScheduleKey: string = "every_30_mins";
  selectedGroupKey: string = "";

  private currentMessage: Subscription;
  private pendingRuleKeys: Set<string> = new Set();
  private selectedGroupBySchedule: Record<string, string> = {};

  private scheduleNameMap: Record<string, string> = {
    every_30_mins: "Every 30 Mins",
    daily_once: "Daily Once",
  };

  private groupNameMap: Record<string, string> = {
    network_rules: "Network",
    database_rules: "Database",
    governance_rules: "Governance",
    daily_spend_rules: "Daily Spend",
    daily_governance_rules: "Daily Governance",
    daily_cost_rules: "Daily Cost",
  };

  constructor(private apiService: APIService, private notifier: NotifierService) {}

  ngOnInit(): void {
    this.currentMessage = this.notifier.currentMessage.subscribe((msg) => {
      if (!msg) {
        return;
      }

      let data: any = null;
      try {
        data = JSON.parse(msg);
      } catch {
        return;
      }

      if (data.value == null || data.source != "notifier") {
        return;
      }
      if (data.key == "accountId") {
        this.subscriptionId = data.value;
        this.load();
      }
    });

    this.notifier.loading(true);
    this.load().finally(() => {
      this.notifier.loading(false);
    });
  }

  ngOnDestroy(): void {
    if (this.currentMessage) {
      this.currentMessage.unsubscribe();
    }
  }

  get selectedSchedule(): AzureTellMeSchedule {
    return this.scheduleData.find((item: AzureTellMeSchedule) => item.key == this.selectedScheduleKey);
  }

  get selectedGroup(): AzureTellMeGroup {
    if (!this.selectedSchedule) {
      return null;
    }
    return this.selectedSchedule.groups.find((item: AzureTellMeGroup) => item.key == this.selectedGroupKey);
  }

  get pendingChangesCount(): number {
    return this.pendingRuleKeys.size;
  }

  addAdditionalContact() {
    const lastValue = this.additionalContacts[this.additionalContacts.length - 1] || "";
    if (lastValue.trim() == "") {
      return;
    }
    this.additionalContacts.push("");
  }

  trackByContactIndex(index: number) {
    return index;
  }

  totalAdditionalContacts(): number {
    return this.additionalContacts.filter((contact: string) => contact?.trim() != "").length;
  }

  hasNewEmailIds(): boolean {
    return this.getNewEmailIds().length > 0;
  }

  selectSchedule(scheduleKey: string) {
    this.selectedScheduleKey = scheduleKey;
    const schedule = this.scheduleData.find((item: AzureTellMeSchedule) => item.key == scheduleKey);
    this.selectedGroupKey = this.getSelectedGroupForSchedule(schedule);
    if (this.selectedGroupKey) {
      this.selectedGroupBySchedule[scheduleKey] = this.selectedGroupKey;
    }
  }

  selectGroup(scheduleKey: string, groupKey: string) {
    this.selectedScheduleKey = scheduleKey;
    this.selectedGroupKey = groupKey;
    this.selectedGroupBySchedule[scheduleKey] = groupKey;
  }

  isGroupActive(scheduleKey: string, groupKey: string): boolean {
    return scheduleKey == this.selectedScheduleKey && groupKey == this.selectedGroupKey;
  }

  private getSelectedGroupForSchedule(schedule: AzureTellMeSchedule): string {
    if (!schedule || schedule.groups.length == 0) {
      return "";
    }

    const savedGroupKey = this.selectedGroupBySchedule[schedule.key];
    const savedGroupExists = schedule.groups.some(
      (group: AzureTellMeGroup) => group.key == savedGroupKey
    );

    if (savedGroupExists) {
      return savedGroupKey;
    }

    return schedule.groups[0].key;
  }

  async load() {
    this.userId = localStorage.getItem("selectedClientList");
    this.subscriptionId = localStorage.getItem("accountId");
    this.urlPrefix = localStorage.getItem("role") == "Admin" ? "admin" : "client";

    if (!this.userId || !this.subscriptionId) {
      this.loading = false;
      this.errorMessage = "Unable to load Tell Me. Missing user or subscription context.";
      return;
    }

    this.loading = true;
    this.errorMessage = null;
    this.scheduleData = [];
    this.pendingRuleKeys.clear();
    this.additionalContacts = [""];
    this.existingEmailIds = [];

    try {
      const header = {
        Authorization: localStorage.getItem("t"),
      };

      const statusUrl = `${this.buildTellMeUrl("rule-status")}&_ts=${Date.now()}`;
      const emailsUrl = `${this.buildAlertEmailsUrl("emails")}&_ts=${Date.now()}`;

      const [statusResult, emailsResult] = await Promise.all([
        this.apiService.getDataPromis(statusUrl, {}, header),
        this.apiService.getDataPromis(emailsUrl, {}, header),
      ]);
      const statusSchedule = statusResult?.rule_statuses?.schedule || {};

      this.scheduleData = this.buildScheduleData({}, statusSchedule);
      this.existingEmailIds = Array.isArray(emailsResult?.email_ids) ? emailsResult.email_ids : [];
      this.additionalContacts =
        this.existingEmailIds.length > 0
          ? [...this.existingEmailIds, ""]
          : [""];

      if (this.scheduleData.length > 0) {
        const availableSchedules = new Set(
          this.scheduleData.map((schedule: AzureTellMeSchedule) => schedule.key)
        );
        Object.keys(this.selectedGroupBySchedule).forEach((scheduleKey: string) => {
          if (!availableSchedules.has(scheduleKey)) {
            delete this.selectedGroupBySchedule[scheduleKey];
          }
        });

        const selected =
          this.scheduleData.find((item: AzureTellMeSchedule) => item.key == this.selectedScheduleKey) ||
          this.scheduleData[0];

        this.selectedScheduleKey = selected.key;
        this.selectedGroupKey = this.getSelectedGroupForSchedule(selected);
        if (this.selectedGroupKey) {
          this.selectedGroupBySchedule[selected.key] = this.selectedGroupKey;
        }
      } else {
        this.selectedGroupKey = "";
      }
    } catch (error) {
      this.errorMessage = "Unable to load Azure Tell Me rules.";
      this.notifier.alert("Error", "", this.errorMessage, "error", 5000);
    } finally {
      this.loading = false;
    }
  }

  onRuleStatusChange(rule: AzureTellMeRule) {
    const ruleKey = this.getRuleKey(rule);
    if (rule.enabled == rule.initialEnabled) {
      this.pendingRuleKeys.delete(ruleKey);
      return;
    }
    this.pendingRuleKeys.add(ruleKey);
  }

  async submitChanges(event: any = null) {
    if (event) {
      event.stopPropagation();
    }

    if (!this.writeAccess) {
      this.notifier.alert("Info", "", "You do not have write access for this action.", "info", 5000);
      return;
    }

    const pendingRules = this.getPendingRules();
    if (pendingRules.length == 0) {
      this.notifier.alert("Info", "", "No status changes to submit.", "info", 3000);
      return;
    }

    this.savingTellMe = true;
    const failedRules: string[] = [];

    for (const rule of pendingRules) {
      rule.saving = true;

      try {
        await this.upsertRule(rule, rule.enabled);
        rule.initialEnabled = rule.enabled;
        this.pendingRuleKeys.delete(this.getRuleKey(rule));
      } catch (error: any) {
        failedRules.push(rule.rule_name);
        this.pendingRuleKeys.add(this.getRuleKey(rule));
        rule.enabled = rule.initialEnabled;
      } finally {
        rule.saving = false;
      }
    }

    if (failedRules.length == 0) {
      this.notifier.alert("Success", "", "Configuration updated successfully", "success", 5000);
    } else if (failedRules.length < pendingRules.length) {
      this.notifier.alert(
        "Info",
        "",
        `Saved with partial failures (${failedRules.length}). Please retry the failed rules.`,
        "info",
        5000
      );
    } else {
      this.notifier.alert(
        "Error",
        "",
        "Unable to update Tell Me rule status. Please retry.",
        "error",
        5000
      );
    }

    await this.load();
    this.savingTellMe = false;
  }

  private getPendingRules(): AzureTellMeRule[] {
    const pendingRules: AzureTellMeRule[] = [];

    this.scheduleData.forEach((schedule: AzureTellMeSchedule) => {
      schedule.groups.forEach((group: AzureTellMeGroup) => {
        group.rules.forEach((rule: AzureTellMeRule) => {
          if (this.pendingRuleKeys.has(this.getRuleKey(rule))) {
            pendingRules.push(rule);
          }
        });
      });
    });

    return pendingRules;
  }

  private getRuleKey(rule: AzureTellMeRule): string {
    return `${rule.schedule_key}::${rule.group_key}::${rule.rule_id}`;
  }

  private buildTellMeUrl(path: string): string {
    return (
      `${environment.apiURL}/${this.urlPrefix}/azure/tellme/api/tellme/${path}` +
      `?user_id=${encodeURIComponent(this.userId || "")}` +
      `&subscription_id=${encodeURIComponent(this.subscriptionId || "")}`
    );
  }

  private buildAlertEmailsUrl(path: string): string {
    return (
      `${environment.apiURL}/${this.urlPrefix}/azure/tellme/api/tellme/alerts/${path}` +
      `?subscription_id=${encodeURIComponent(this.subscriptionId || "")}`
    );
  }

  async saveEmails(event: any = null) {
    if (event) {
      event.stopPropagation();
    }

    if (!this.writeAccess) {
      this.notifier.alert("Info", "", "You do not have write access for this action.", "info", 5000);
      return;
    }

    const newEmailIds = this.getNewEmailIds();

    if (newEmailIds.length == 0) {
      return;
    }

    this.savingEmails = true;

    try {
      this.subscriptionId = localStorage.getItem("accountId");

      const header = {
        Authorization: localStorage.getItem("t"),
        accept: "application/json",
        "Content-Type": "application/json",
      };

      const payload = {
        email_ids: newEmailIds,
      };

      const response: any = await this.apiService.postDataPromis(
        this.buildAlertEmailsUrl("emails-upsert"),
        payload,
        header
      );

      const apiStatusMessages: string[] = Array.isArray(response?.details)
        ? Array.from(
            new Set(
              response.details
                .map((item: any) =>
                  String(item?.status || item?.message || item?.error_message || item?.error || "").trim()
                )
                .filter((status: string) => status != "")
            )
          )
        : [];

      const validationMessages: string[] = Array.isArray(response?.detail)
        ? Array.from(
            new Set(
              response.detail
                .map((item: any) =>
                  String(item?.msg || item?.message || item?.error_message || item?.error || "").trim()
                )
                .filter((msg: string) => msg != "")
            )
          )
        : [];

      if (validationMessages.length > 0) {
        this.notifier.alert("Error", "", validationMessages.join(", "), "error", 5000);
        return;
      }

      const backendErrorMessage = String(response?.error_message || response?.error || "").trim();
      if (backendErrorMessage && apiStatusMessages.length == 0) {
        throw new Error(backendErrorMessage);
      }

      const backendMessage = String(response?.message || "").trim();
      const backendMessages: string[] =
        apiStatusMessages.length > 0
          ? apiStatusMessages
          : backendMessage
            ? [backendMessage]
            : [];

      const hasHardFailure = backendMessages.some((message) =>
        /invalid|error|failed|unable|denied|not allowed|not valid/i.test(String(message || ""))
      );
      const hasSoftNotice = backendMessages.some((message) =>
        /already exists|exists/i.test(String(message || ""))
      );
      const hasSuccess = backendMessages.some((message) =>
        /success|created|updated|added/i.test(String(message || ""))
      );

      let messageType: "success" | "info" | "error" = "success";
      let messageTitle: "Success" | "Info" | "Error" = "Success";
      if (hasHardFailure) {
        messageType = hasSuccess ? "info" : "error";
        messageTitle = hasSuccess ? "Info" : "Error";
      } else if (hasSoftNotice && !hasSuccess) {
        messageType = "info";
        messageTitle = "Info";
      }

      const refreshedEmails: any = await this.apiService.getDataPromis(
        this.buildAlertEmailsUrl("emails"),
        {},
        header
      );
      this.existingEmailIds = Array.isArray(refreshedEmails?.email_ids) ? refreshedEmails.email_ids : [];
      this.additionalContacts =
        this.existingEmailIds.length > 0
          ? [...this.existingEmailIds, ""]
          : [""];

      if (backendMessages.length > 0) {
        this.notifier.alert(messageTitle, "", backendMessages.join(", "), messageType, 5000);
      }
    } catch (error: any) {
      const message = String(error?.error_message || error?.error || error?.message || error || "").trim();
      if (message) {
        this.notifier.alert("Error", "", message, "error", 5000);
      }
    } finally {
      this.savingEmails = false;
    }
  }

  private getNewEmailIds(): string[] {
    const existing = new Set(
      (this.existingEmailIds || [])
        .map((email: string) => (email || "").trim().toLowerCase())
        .filter((email: string) => email != "")
    );

    const seen = new Set<string>();
    const newEmails: string[] = [];

    this.additionalContacts.forEach((email: string) => {
      const normalized = (email || "").trim();
      if (normalized == "") {
        return;
      }

      const key = normalized.toLowerCase();
      if (existing.has(key) || seen.has(key)) {
        return;
      }

      seen.add(key);
      newEmails.push(normalized);
    });

    return newEmails;
  }

  private buildScheduleData(rulesSchedule: any, statusSchedule: any): AzureTellMeSchedule[] {
    const preferredScheduleOrder = ["every_30_mins", "daily_once"];
    const scheduleKeys = this.mergeKeys(preferredScheduleOrder, [
      ...Object.keys(rulesSchedule || {}),
      ...Object.keys(statusSchedule || {}),
    ]);

    const scheduleData = scheduleKeys.map((scheduleKey: string) => {
      const rulesGroups = rulesSchedule?.[scheduleKey] || {};
      const statusGroups = statusSchedule?.[scheduleKey] || {};

      const groupKeys = this.mergeKeys([], [...Object.keys(rulesGroups), ...Object.keys(statusGroups)]);

      const groups: AzureTellMeGroup[] = groupKeys.map((groupKey: string) => {
        const baseRules = Array.isArray(rulesGroups[groupKey]) ? rulesGroups[groupKey] : [];
        const statusRules = Array.isArray(statusGroups[groupKey]) ? statusGroups[groupKey] : [];

        const byRuleId: Map<string, any> = new Map();

        baseRules.forEach((rule: any) => {
          if (!rule?.rule_id) {
            return;
          }
          byRuleId.set(rule.rule_id, { ...rule });
        });

        statusRules.forEach((rule: any) => {
          if (!rule?.rule_id) {
            return;
          }
          const current = byRuleId.get(rule.rule_id) || {};
          byRuleId.set(rule.rule_id, { ...current, ...rule });
        });

        const mergedRules: AzureTellMeRule[] = Array.from(byRuleId.values())
          .map((rule: any, index: number) => {
            const enabled = this.getRuleEnabled(rule);
            return {
              rule_id: rule.rule_id,
              rule_name: rule.rule_name || rule.name || "-",
              s_no: Number(rule.s_no) || index + 1,
              enabled,
              initialEnabled: enabled,
              schedule_key: scheduleKey,
              group_key: groupKey,
              rule_group_path: `${scheduleKey}.${groupKey}`,
            };
          })
          .sort((a: AzureTellMeRule, b: AzureTellMeRule) => a.s_no - b.s_no);

        return {
          key: groupKey,
          name: this.groupNameMap[groupKey] || this.toTitle(groupKey),
          rules: mergedRules,
        };
      });

      return {
        key: scheduleKey,
        name: this.scheduleNameMap[scheduleKey] || this.toTitle(scheduleKey),
        groups,
      };
    });

    return scheduleData.filter((schedule: AzureTellMeSchedule) => schedule.groups.length > 0);
  }

  private getRuleEnabled(rule: any): boolean {
    if (typeof rule?.enabled == "boolean") {
      return rule.enabled;
    }
    if (typeof rule?.is_enabled == "boolean") {
      return rule.is_enabled;
    }
    if (typeof rule?.isEnabled == "boolean") {
      return rule.isEnabled;
    }

    const statusValue = rule?.status ?? rule?.rule_status ?? rule?.state;
    if (typeof statusValue == "string") {
      const normalized = statusValue.toLowerCase();
      if (["enabled", "enable", "active", "true", "1"].includes(normalized)) {
        return true;
      }
      if (["disabled", "disable", "inactive", "false", "0"].includes(normalized)) {
        return false;
      }
    }

    if (typeof rule?.enabled == "string") {
      const normalized = rule.enabled.toLowerCase();
      return ["enabled", "enable", "active", "true", "1"].includes(normalized);
    }
    return false;
  }

  private mergeKeys(preferredOrder: string[], keys: string[]): string[] {
    const uniqueKeys = Array.from(new Set(keys.filter((key: string) => !!key)));
    const ordered = preferredOrder.filter((key: string) => uniqueKeys.includes(key));
    uniqueKeys.forEach((key: string) => {
      if (!ordered.includes(key)) {
        ordered.push(key);
      }
    });
    return ordered;
  }

  private toTitle(value: string): string {
    return value
      .split("_")
      .map((token: string) => token.charAt(0).toUpperCase() + token.slice(1))
      .join(" ");
  }

  private async upsertRule(rule: AzureTellMeRule, enabled: boolean) {
    const header = {
      Authorization: localStorage.getItem("t"),
    };

    const endpoint = this.buildTellMeUrl("rules/upsert");
    const candidatePaths = this.mergeKeys([], [
      `${rule.schedule_key}.${rule.group_key}`,
      `schedule.${rule.schedule_key}.${rule.group_key}`,
    ]);

    let lastError: string = "Unable to update Tell Me rule status.";

    for (const rulePath of candidatePaths) {
      const payloadCandidates = [
        {
          rules: [
            {
              rule_id: rule.rule_id,
              rule_name: rule.rule_name,
              enabled,
              status: enabled ? "enabled" : "disabled",
              rule_group_path: rulePath,
            },
          ],
        },
        {
          rules: [
            {
              rule_id: rule.rule_id,
              enabled,
              rule_group_path: rulePath,
            },
          ],
        },
        {
          rule_id: rule.rule_id,
          rule_name: rule.rule_name,
          enabled,
          rule_group_path: rulePath,
        },
      ];

      for (const payload of payloadCandidates) {
        const result: any = await this.apiService.postDataPromis(endpoint, payload, header);
        const isResultObject = !!result && typeof result == "object" && !Array.isArray(result);
        const hasValidationError = Array.isArray(result?.detail) && result.detail.length > 0;
        const hasErrorMessage = !!result?.error || !!result?.error_message || !!result?.message;
        const isExplicitSuccess =
          result?.status == 1 || result?.status == "1" || result?.s == 1 || result?.s == "1" || result?.success == true;
        const isSuccessResponse = isExplicitSuccess || (isResultObject && !hasValidationError && !hasErrorMessage);

        if (isSuccessResponse) {
          rule.rule_group_path = rulePath;
          if (typeof result?.enabled == "boolean") {
            rule.enabled = result.enabled;
          }
          return;
        }

        if (hasValidationError) {
          lastError = result.detail[0]?.msg || lastError;
        } else if (hasErrorMessage) {
          lastError = result?.error_message || result?.error || result?.message || lastError;
        }
      }
    }

    throw new Error(lastError);
  }
}
